import { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Badge, Table, MobileCardList } from '../components/common';

interface Zone {
  id: string;
  name: string;
  space: string;
  status: 'Available' | 'Occupied';
  storageType: string;
  client?: string;
  makeInactive?: boolean;
}

interface Client {
  id: string;
  name: string;
  zone: string;
  space: string;
  contractStart: string;
  contractEnd: string;
  status: 'Active' | 'Inactive';
}

export default function WarehouseLocation() {
  const [zones] = useState<Zone[]>([
    {
      id: '1',
      name: 'Zone A',
      space: '2,500 sq ft',
      status: 'Available',
      storageType: 'Storage Type: Normal',
    },
    {
      id: '2',
      name: 'Zone B',
      space: '2,000 sq ft',
      status: 'Occupied',
      storageType: 'Client: ABC Electronics',
      client: 'ABC Electronics',
      makeInactive: true,
    },
    {
      id: '3',
      name: 'Zone C',
      space: '4,000 sq ft',
      status: 'Available',
      storageType: 'Storage Type: Cold Storage',
    },
    {
      id: '4',
      name: 'Zone D',
      space: '2,000 sq ft',
      status: 'Occupied',
      storageType: 'Client: XYZ Textiles',
      client: 'XYZ Textiles',
      makeInactive: true,
    },
    {
      id: '5',
      name: 'Zone E',
      space: '3,500 sq ft',
      status: 'Available',
      storageType: 'Storage Type: Normal',
    },
    {
      id: '6',
      name: 'Zone F',
      space: '2,500 sq ft',
      status: 'Available',
      storageType: 'Storage Type: Hazardous',
    },
  ]);

  const [clients] = useState<Client[]>([
    {
      id: '1',
      name: 'ABC Electronics Pvt Ltd',
      zone: 'Zone B',
      space: '2,000 sq ft',
      contractStart: 'Jan 15, 2026',
      contractEnd: 'Jan 15, 2027',
      status: 'Active',
    },
    {
      id: '2',
      name: 'XYZ Textiles Ltd',
      zone: 'Zone D',
      space: '2,000 sq ft',
      contractStart: 'Feb 01, 2026',
      contractEnd: 'Aug 01, 2026',
      status: 'Active',
    },
  ]);

  const totalSpace = 50000;
  const availableSpace = 12500;
  const occupiedSpace = 37500;
  const activeClients = 15;

  const clientColumns = [
    {
      key: 'name',
      label: 'CLIENT NAME',
    },
    {
      key: 'zone',
      label: 'ZONE',
      render: (value: string) => (
        <Badge variant="info" rounded>{value}</Badge>
      ),
    },
    {
      key: 'space',
      label: 'SPACE',
    },
    {
      key: 'contractStart',
      label: 'CONTRACT START',
    },
    {
      key: 'contractEnd',
      label: 'CONTRACT END',
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (value: string) => (
        <Badge variant="success" rounded>{value}</Badge>
      ),
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      align: 'center' as const,
      render: () => (
        <button className="flex items-center text-gray-700 hover:text-brand-600 transition-colors text-sm">
          <i className="fas fa-times-circle mr-1"></i>
          Deactivate
        </button>
      ),
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Warehouse Locations</h1>
          <p className="text-gray-600 mt-1">Manage warehouse space allocation</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-warehouse text-cyan-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">TOTAL SPACE</p>
            <p className="text-3xl font-bold text-gray-900">{totalSpace.toLocaleString()} sq ft</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-check-circle text-green-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">AVAILABLE</p>
            <p className="text-3xl font-bold text-gray-900">{availableSpace.toLocaleString()} sq ft</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-box text-orange-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">OCCUPIED</p>
            <p className="text-3xl font-bold text-gray-900">{occupiedSpace.toLocaleString()} sq ft</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-users text-brand-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">ACTIVE CLIENTS</p>
            <p className="text-3xl font-bold text-gray-900">{activeClients}</p>
          </div>
        </div>

        {/* Warehouse Layout */}
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center mb-6">
            <i className="fas fa-map text-brand-600 mr-2"></i>
            <h3 className="text-lg font-semibold text-gray-900">Warehouse Layout</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {zones.map((zone) => (
              <div
                key={zone.id}
                className={`rounded-xl p-6 border-2 transition-all ${
                  zone.status === 'Available'
                    ? 'border-gray-200 bg-white hover:border-brand-300'
                    : 'border-brand-400 bg-brand-50'
                }`}
              >
                {/* Zone Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <i className="fas fa-warehouse text-gray-500 text-2xl"></i>
                  </div>
                </div>

                {/* Zone Name */}
                <h4 className="text-xl font-bold text-gray-900 text-center mb-2">
                  {zone.name}
                </h4>

                {/* Space */}
                <p className="text-center text-gray-600 mb-3">{zone.space}</p>

                {/* Status Badge */}
                <div className="flex justify-center mb-3">
                  <Badge
                    variant={zone.status === 'Available' ? 'success' : 'warning'}
                    rounded
                  >
                    {zone.status}
                  </Badge>
                </div>

                {/* Storage Type / Client */}
                <p className="text-center text-sm text-gray-600 mb-4">
                  {zone.storageType}
                </p>

                {/* Make Inactive Button */}
                {zone.makeInactive && (
                  <button className="w-full flex items-center justify-center text-sm text-gray-700 hover:text-red-600 transition-colors">
                    <i className="fas fa-times-circle mr-2"></i>
                    Make Inactive
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active Clients Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center px-6 py-4 border-b border-gray-100">
            <i className="fas fa-users text-brand-600 mr-2"></i>
            <h3 className="text-lg font-semibold text-gray-900">Active Clients</h3>
          </div>

          <div className="hidden lg:block">
            <Table
              columns={clientColumns}
              data={clients}
              hoverable
            />
          </div>
          <div className="p-3 lg:hidden">
            <MobileCardList columns={clientColumns} data={clients} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
